// Cache-bust marker (must be runtime statement so Vite includes it in bundle hash):
// forces a new main bundle filename to bypass a poisoned Cloudflare cache entry
// where /assets/main-<hash>.js was returning text/html when Origin header matched.
const __CACHE_BUST_MARKER__ = '2026-04-15-poison-cache-bust';
if (typeof window !== 'undefined') (window as any).__appBuildMarker = __CACHE_BUST_MARKER__;
import React, { useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppStore } from './stores/appStore';
import { useAccessibility } from './services/accessibility';
import { installRuntimeRecoveryListeners } from './services/runtimeRecovery';
import ControlTextLocalizer from './i18n/ControlTextLocalizer';
// Import feedbackService to trigger preloading on app start
import './services/feedback';

installRuntimeRecoveryListeners();

const APP_FONT_FAMILY = '"Orbitron", monospace';

// Create theme based on mode
const createAppTheme = (darkMode: boolean) => createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    primary: {
      main: '#60a5fa', // Blue for primary actions
      light: '#93c5fd',
      dark: '#3b82f6',
    },
    secondary: {
      main: '#34d399', // Green for secondary actions
      light: '#6ee7b7',
      dark: '#10b981',
    },
    error: {
      main: '#ef4444',
    },
    warning: {
      main: '#f59e0b',
    },
    info: {
      main: '#06b6d4',
    },
    success: {
      main: '#22c55e',
    },
    background: {
      default: darkMode ? '#0f172a' : '#f0f4ff',
      paper: darkMode ? '#1e293b' : '#ffffff',
    },
    text: {
      primary: darkMode ? '#f8fafc' : '#0f172a',
      secondary: darkMode ? 'rgba(248, 250, 252, 0.7)' : 'rgba(15, 23, 42, 0.7)',
    },
  },
  typography: {
    fontFamily: APP_FONT_FAMILY,
    allVariants: {
      fontFamily: APP_FONT_FAMILY,
    },
    h1: {
      fontWeight: 800,
      letterSpacing: 0,
    },
    h2: {
      fontWeight: 700,
      letterSpacing: 0,
    },
    h3: {
      fontWeight: 650,
      letterSpacing: 0,
    },
    h4: {
      fontWeight: 600,
      letterSpacing: 0,
    },
    h5: {
      fontWeight: 550,
      letterSpacing: 0,
    },
    h6: {
      fontWeight: 500,
      letterSpacing: 0,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: 0,
    },
    caption: {
      fontWeight: 400,
    },
    overline: {
      fontWeight: 500,
      letterSpacing: 0,
      textTransform: 'uppercase',
    },
  },
  zIndex: {
    snackbar: 2147483647, // Ensure notifications appear above Telegram chrome and dialogs
  },
  shape: {
    borderRadius: 12, // Rounded corners throughout
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.1)',
    '0px 4px 8px rgba(0,0,0,0.15)',
    '0px 8px 16px rgba(0,0,0,0.2)',
    '0px 12px 24px rgba(0,0,0,0.25)',
    '0px 16px 32px rgba(0,0,0,0.3)',
    '0px 20px 40px rgba(0,0,0,0.35)',
    '0px 24px 48px rgba(0,0,0,0.4)',
    '0px 28px 56px rgba(0,0,0,0.45)',
    '0px 32px 64px rgba(0,0,0,0.5)',
    '0px 36px 72px rgba(0,0,0,0.55)',
    '0px 40px 80px rgba(0,0,0,0.6)',
    '0px 44px 88px rgba(0,0,0,0.65)',
    '0px 48px 96px rgba(0,0,0,0.7)',
    '0px 52px 104px rgba(0,0,0,0.75)',
    '0px 56px 112px rgba(0,0,0,0.8)',
    '0px 60px 120px rgba(0,0,0,0.85)',
    '0px 64px 128px rgba(0,0,0,0.9)',
    '0px 68px 136px rgba(0,0,0,0.95)',
    '0px 72px 144px rgba(0,0,0,1)',
    '0px 76px 152px rgba(0,0,0,1)',
    '0px 80px 160px rgba(0,0,0,1)',
    '0px 84px 168px rgba(0,0,0,1)',
    '0px 88px 176px rgba(0,0,0,1)',
    '0px 92px 184px rgba(0,0,0,1)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          fontFamily: `${APP_FONT_FAMILY} !important`,
        },
        html: {
          fontFamily: APP_FONT_FAMILY,
        },
        body: {
          fontFamily: APP_FONT_FAMILY,
        },
        'button, input, textarea, select, option, optgroup': {
          fontFamily: `${APP_FONT_FAMILY} !important`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.92)' : 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(16px)',
          color: '#f1f5f9',
          fontSize: '0.8rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          border: darkMode ? '1px solid rgba(96, 165, 250, 0.3)' : '1px solid rgba(51, 65, 85, 0.4)',
          borderRadius: 8,
          padding: '8px 14px',
          boxShadow: darkMode
            ? '0 4px 20px rgba(0,0,0,0.6), 0 0 8px rgba(96,165,250,0.15)'
            : '0 4px 16px rgba(0,0,0,0.3)',
        },
        arrow: {
          color: darkMode ? 'rgba(15, 23, 42, 0.92)' : 'rgba(30, 41, 59, 0.95)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Disable default Material gradient
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          zIndex: 2147483647, // Ensure notifications appear above Telegram chrome and dialogs
        },
      },
    },
  },
});

// Theme wrapper component that responds to store changes and accessibility
function ThemedApp() {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const toggleHighContrast = useAppStore((state) => state.toggleHighContrast);
  const accessibilityPrefs = useAccessibility();
  
  const theme = useMemo(() => createAppTheme(darkMode), [darkMode]);
  
  // Auto-enable high contrast if system prefers it
  useEffect(() => {
    if (accessibilityPrefs.prefersHighContrast && !highContrast) {
      // System prefers high contrast but app doesn't have it enabled
      // Auto-enable it for better accessibility
      toggleHighContrast();
      console.log('[M3 Accessibility] Auto-enabled high contrast mode based on system preference');
    }
  }, [accessibilityPrefs.prefersHighContrast]);
  
  // Apply high contrast class to document
  useEffect(() => {
    const root = document.documentElement;
    if (highContrast || accessibilityPrefs.prefersHighContrast) {
      root.classList.add('m3-high-contrast');
    } else {
      root.classList.remove('m3-high-contrast');
    }
  }, [highContrast, accessibilityPrefs.prefersHighContrast]);
  
  // Initialize feedback service on first interaction
  useEffect(() => {
    // feedbackService is auto-initialized on first user interaction
    // This log confirms it's loaded
    console.log('[M3 Feedback] Service loaded, will initialize on first interaction');
  }, []);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ControlTextLocalizer />
      <App />
    </ThemeProvider>
  );
}

// Wait for Telegram WebApp to be ready
const init = () => {
  const root = document.getElementById('root');
  if (!root) {
    console.error('Root element not found');
    return;
  }

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <BrowserRouter>
        <ThemedApp />
      </BrowserRouter>
    </React.StrictMode>
  );
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
